import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Clock, Broom, Users, Hourglass, X, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useVisitAllocation } from '../hooks/useVisitAllocation';
import WorkerAllocation from '../components/WorkerAllocation';
import '../pages/ProviderDashboard.css';
import './Schedule.css';

const formatDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getTodayStr = () => formatDateStr(new Date());

const getDatePlusDaysStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return formatDateStr(d);
};

const Schedule = () => {
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const allocation = useVisitAllocation((visitId, assignment) => {
    setSchedule((current) => current.map((item) => String(item.id) === String(visitId)
      ? { ...item, workerId: assignment.workerId, worker: assignment.worker }
      : item));
  });
  const { workers, workersError } = allocation;
  const [loading, setLoading] = useState(true);
  const [customersList, setCustomersList] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const workerFilter = searchParams.get('worker');
  const serviceFilter = searchParams.get('service');
  const recurrenceClientId = searchParams.get('clientId');
  const customerFilter = searchParams.get('customer');
  const quickFilter = searchParams.get('filter'); // e.g. 'upcoming', 'today', 'unallocated', 'overdue', 'incident'

  // Advanced filters - Default view to 1 week (Today to Today + 7 days)
  const [selectedCustomer, setSelectedCustomer] = useState(customerFilter || '');
  const [selectedWorker, setSelectedWorker] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    if (quickFilter === 'today') return getTodayStr();
    if (quickFilter === 'overdue') return getDatePlusDaysStr(-7);
    return getTodayStr();
  });
  const [dateTo, setDateTo] = useState(() => {
    if (serviceFilter) return '';
    if (quickFilter === 'today') return getTodayStr();
    if (quickFilter === 'overdue') return getTodayStr();
    return getDatePlusDaysStr(7);
  });
  const [visitLimit, setVisitLimit] = useState(() => searchParams.get('limit') || (serviceFilter ? '30' : '100'));
  const [sortConfig, setSortConfig] = useState({ key: 'dateTime', direction: 'asc' });

  const [workerName, setWorkerName] = useState(null);
  const activeRoleHeader = (activeUserRoles || []).join(',');
  const selectedCustomerId = selectedCustomer
    ? (selectedCustomer === customerFilter && recurrenceClientId)
      || customersList.find((customer) => customer.name === selectedCustomer || customer.id === selectedCustomer)?.id
      || selectedCustomer
    : '';


  useEffect(() => {
    if (serviceFilter) {
      setDateFrom(getTodayStr());
      setDateTo('');
      setVisitLimit(searchParams.get('limit') || '30');
    } else if (quickFilter === 'today') {
      setDateFrom(getTodayStr());
      setDateTo(getTodayStr());
    } else if (quickFilter === 'upcoming') {
      setDateFrom(getTodayStr());
      setDateTo(getDatePlusDaysStr(7));
    } else if (quickFilter === 'overdue') {
      setDateFrom(getDatePlusDaysStr(-7));
      setDateTo(getTodayStr());
    }
  }, [quickFilter, serviceFilter, searchParams]);

  useEffect(() => {
    if (authLoading) return;
    const controller = new AbortController();
    fetch(`/api/customers?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
      signal: controller.signal,
      headers: {
        'x-kcs-user-email': activeUserEmail || '',
        'x-kcs-user-roles': activeRoleHeader,
        'x-kcs-provider-employee-id': activeEmployeeId || '167',
      },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (controller.signal.aborted) return;
        setCustomersList(
          Array.isArray(data)
            ? data.map((c) => ({
                id: String(c.id),
                name: c.displayName || c.name || `Customer ${c.id}`,
              }))
            : []
        );
      })
      .catch(() => {
        if (!controller.signal.aborted) setCustomersList([]);
      });
    return () => controller.abort();
  }, [authLoading, activeUserEmail, activeRoleHeader, activeEmployeeId]);

  useEffect(() => {
    // Fetch worker name if filtering by worker ID via URL
    if (workerFilter) {
      fetch(`/api/workers/${workerFilter}`)
        .then(res => res.json())
        .then(worker => {
          const fullName = `${worker.firstName} ${worker.lastName}`;
          setWorkerName(fullName);
          setSelectedWorker(fullName);
        })
        .catch(err => console.error('Error fetching worker:', err));
    }

    // Set customer filter from URL parameter if present
    if (customerFilter) {
      setSelectedCustomer(decodeURIComponent(customerFilter));
    }
  }, [workerFilter, customerFilter]);

  useEffect(() => {
    if (authLoading) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchSchedule = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams({
          employeeId: activeEmployeeId || '167',
          limit: String(Math.min(500, Math.max(1, parseInt(visitLimit, 10) || 100))),
        });
        if (serviceFilter) params.set('serviceId', serviceFilter);

        // Pass date range to API if provided
        if (dateFrom) {
          params.set('start', dateFrom);
        }
        if (dateTo) {
          params.set('end', dateTo);
        } else {
          params.set('openEnded', 'true');
        }

        // Pass patient filter to API if selected
        if (selectedCustomerId) {
          params.set('patientId', selectedCustomerId);
        }

        const res = await fetch(`/api/shifts?${params.toString()}`, {
          signal: controller.signal,
          headers: {
            'x-kcs-user-email': activeUserEmail || '',
            'x-kcs-user-roles': activeRoleHeader,
            'x-kcs-provider-employee-id': activeEmployeeId || '167',
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch schedule: ${res.status}`);
        }

        const data = await res.json();
        const shifts = data.shifts || [];

        if (isMounted) {
          setSchedule(shifts);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading schedule:', err);
          if (isMounted) {
            setSchedule([]);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSchedule();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [authLoading, activeEmployeeId, activeUserEmail, activeRoleHeader, dateFrom, dateTo, selectedCustomerId, serviceFilter, visitLimit]);

  const customerOptions = useMemo(() => {
    const list = [...customersList];
    // Also include any customer names present in the current schedule payload
    schedule.forEach((item) => {
      if (item.customer && !list.some((c) => c.name === item.customer)) {
        list.push({ id: String(item.customerId || item.customer), name: item.customer });
      }
    });
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [customersList, schedule]);

  const renderWorkerAllocation = (item) => (
    <WorkerAllocation item={item} {...allocation} saving={allocation.savingAssignments[item.id]} error={allocation.assignmentErrors[item.id]} />
  );

  const getSortValue = (item, key) => {
    switch (key) {
      case 'dateTime':
        return new Date(item.raw?.start || item.scheduledDate || item.dateTime || 0).getTime();
      case 'duration':
        return Number((item.duration || '').replace(/[^0-9]/g, '')) || 0;
      case 'customer':
        return (item.customer || '').toLowerCase();
      case 'serviceType':
        return (item.serviceType || item.service || '').toLowerCase();
      case 'worker':
        return (item.worker || '').toLowerCase();
      case 'status':
        return (item.status || '').toLowerCase();
      default:
        return '';
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Apply all filters
  const filteredSchedule = schedule.filter(item => {
    const effectiveWorker = item.worker;

    // Quick filter from dashboard cards (unallocated, overdue)
    if (quickFilter === 'unallocated' && effectiveWorker) {
      return false;
    }
    if (quickFilter === 'overdue' && !String(item.status || '').toLowerCase().includes('overdue') && !String(item.status || '').toLowerCase().includes('missed')) {
      return false;
    }

    // Customer filter
    if (serviceFilter && String(item.serviceId) !== serviceFilter) {
      return false;
    }
    if (selectedCustomer && item.customer !== selectedCustomer && String(item.customerId) !== String(selectedCustomerId)) {
      return false;
    }

    // Worker filter (from dropdown or URL parameter)
    const workerToFilter = selectedWorker || (workerFilter && workerName ? workerName : null);
    if (workerToFilter && effectiveWorker !== workerToFilter) {
      return false;
    }

    // Date range filter
    if (dateFrom || dateTo) {
      const itemDateStr = item.scheduledDate || getTodayStr();
      if (dateFrom && itemDateStr < dateFrom) return false;
      if (dateTo && itemDateStr > dateTo) return false;
    }

    return true;
  });

  const sortedSchedule = [...filteredSchedule].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const clearWorkerFilter = () => {
    setSelectedWorker('');
    if (workerFilter) {
      navigate('/provider/schedule');
    }
  };

  const clearCustomerFilter = () => {
    setSelectedCustomer('');
    if (customerFilter) {
      navigate('/provider/schedule');
    }
  };

  const clearQuickFilter = () => {
    navigate('/provider/schedule');
  };

  const hasActiveFilters = selectedCustomer || selectedWorker || dateFrom || dateTo || workerFilter || customerFilter || quickFilter;

  const handleRowClick = (e, itemId) => {
    if (e.target.closest('select') || e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    navigate(`/provider/services/${itemId}/edit-job`);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Done': 'status-completed',
      'Completed': 'status-completed',
      'Not done': 'status-overdue',
      'Cancelled': 'status-default',
      'Confirmed': 'status-confirmed',
      'Alert': 'status-amber',
      'Due': 'status-due',
      'Overdue': 'status-overdue',
    };
    return statusMap[status] || 'status-default';
  };

  if (loading) {
    return <div className="loading">Loading schedule...</div>;
  }

  return (
    <div className="provider-dashboard">
      <div className="page-header">
        <h1>Schedule</h1>
      </div>
      {workersError && <div role="alert">{workersError}</div>}

      {/* Advanced Filters */}
      <div className="filter-controls">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="customerFilter">Customer</label>
            <select
              id="customerFilter"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="filter-select"
            >
              <option value="">All Customers</option>
              {customerOptions.map((cust) => (
                <option key={cust.id} value={cust.name}>
                  {cust.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="workerFilter">Worker</label>
            <select
              id="workerFilter"
              value={selectedWorker}
              onChange={(e) => setSelectedWorker(e.target.value)}
              className="filter-select"
            >
              <option value="">All Workers</option>
              {(workers || []).map((worker) => (
                <option key={worker.id} value={`${worker.firstName} ${worker.lastName}`}>
                  {worker.firstName} {worker.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="dateFrom">From Date</label>
            <input
              type="date"
              id="dateFrom"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="filter-input"
            />
          </div>

          {!serviceFilter && (
            <div className="filter-group">
              <label htmlFor="dateTo">To Date</label>
              <input
                type="date"
                id="dateTo"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="filter-input"
              />
            </div>
          )}
          <div className="filter-group">
            <label htmlFor="visitLimit">Max visits</label>
            <input
              id="visitLimit"
              type="number"
              min="1"
              max="500"
              value={visitLimit}
              onChange={(event) => setVisitLimit(event.target.value)}
              className="filter-input"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="active-filters">
            <span className="active-filters-label">Active filters:</span>
            {serviceFilter && (
              <span className="filter-tag">
                Service: {serviceFilter}
                <button onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('service');
                  navigate(`/provider/schedule?${next.toString()}`);
                }} className="filter-tag-close" aria-label="Clear service filter">
                  <X size={14} weight="bold" />
                </button>
              </span>
            )}
            {quickFilter && (
              <span className="filter-tag">
                View: {quickFilter}
                <button onClick={clearQuickFilter} className="filter-tag-close">
                  <X size={14} weight="bold" />
                </button>
              </span>
            )}
            {selectedCustomer && (
              <span className="filter-tag">
                Customer: {selectedCustomer}
                <button onClick={clearCustomerFilter} className="filter-tag-close">
                  <X size={14} weight="bold" />
                </button>
              </span>
            )}
            {selectedWorker && (
              <span className="filter-tag">
                Worker: {selectedWorker}
                <button onClick={clearWorkerFilter} className="filter-tag-close">
                  <X size={14} weight="bold" />
                </button>
              </span>
            )}
            {dateFrom && (
              <span className="filter-tag">
                From: {dateFrom}
                <button onClick={() => setDateFrom('')} className="filter-tag-close">
                  <X size={14} weight="bold" />
                </button>
              </span>
            )}
            {dateTo && (
              <span className="filter-tag">
                To: {dateTo}
                <button onClick={() => setDateTo('')} className="filter-tag-close">
                  <X size={14} weight="bold" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="schedule-summary-bar">
        <span className="schedule-count-text">
          Showing <strong>{sortedSchedule.length}</strong> scheduled visits
        </span>
      </div>

      <div className="table-section">
        {/* Mobile Card View */}
        <div className="mobile-cards">
          {sortedSchedule && sortedSchedule.length > 0 ? (
            sortedSchedule.map((item) => {
              return (
                <div key={item.id} className="service-card">
                  <div className="card-header">
                    <h3 className="card-customer">{item.customer}</h3>
                    <span className={`status-badge ${getStatusClass(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="card-details-compact">
                    <div className="card-info-item">
                      <Clock size={16} weight="regular" className="card-icon" />
                      <span><strong>{item.dateTime || item.time}</strong></span>
                    </div>
                    {item.duration && (
                      <div className="card-info-item">
                        <Hourglass size={16} weight="regular" className="card-icon" />
                        <span>{item.duration}</span>
                      </div>
                    )}
                    <div className="card-info-item">
                      <Broom size={16} weight="regular" className="card-icon" />
                      <span>{item.serviceType || item.service}</span>
                    </div>
                    <div className="card-info-item">
                      <Users size={16} weight="regular" className="card-icon" />
                      {renderWorkerAllocation(item)}
                    </div>
                  </div>
                  <div className="card-actions">
                    <Link to={`/provider/services/${item.id}/edit-job`} className="action-button btn-icon" title="Edit job">
                      <PencilSimple size={24} weight="regular" />
                    </Link>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="empty-state">No scheduled visits found.</p>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort('dateTime')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Date & Time {getSortIndicator('dateTime')}</button></th>
                <th><button type="button" onClick={() => handleSort('duration')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Duration {getSortIndicator('duration')}</button></th>
                <th><button type="button" onClick={() => handleSort('customer')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Customer {getSortIndicator('customer')}</button></th>
                <th><button type="button" onClick={() => handleSort('serviceType')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Service Type {getSortIndicator('serviceType')}</button></th>
                <th><button type="button" onClick={() => handleSort('worker')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Worker Allocation {getSortIndicator('worker')}</button></th>
                <th><button type="button" onClick={() => handleSort('status')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Status {getSortIndicator('status')}</button></th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedSchedule && sortedSchedule.length > 0 ? (
                sortedSchedule.map((item) => {
                  return (
                    <tr key={item.id} className="clickable-row" onClick={(e) => handleRowClick(e, item.id)}>
                      <td>{item.dateTime || item.time}</td>
                      <td>{item.duration || '-'}</td>
                      <td><strong>{item.customer}</strong></td>
                      <td>{item.serviceType || item.service}</td>
                      <td>
                        {renderWorkerAllocation(item)}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <Link 
                          to={`/provider/services/${item.id}/edit-job`}
                          className="action-button btn-icon"
                          title="Edit job"
                        >
                          <PencilSimple size={24} weight="regular" />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No scheduled visits found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Schedule;
